import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form'
import { AssetSpaceDetailFormValue } from '@/components/organisms/settings/guest-app/space-detail-modal'
import { InputField } from '@/components/molecules/input-field'
import { RadioGroupField } from '@/components/molecules/radio-group-field'

type Props = {
  control: Control<AssetSpaceDetailFormValue, any>
  setValue: UseFormSetValue<AssetSpaceDetailFormValue>
}

export const InputSalesStartAndEnd: React.FC<Props> = ({ control, setValue }) => {
  const { t } = useTranslation()

  const watchSalesStart = useWatch({ control, name: 'salesStart' })
  const watchSalesEnd = useWatch({ control, name: 'salesEnd' })

  return (
    <div css={componentStyle}>
      <div css={inputTitleTextStyle}>{t('Sales time')}</div>
      <div>
        <div className="radio-block">
          <div className="label">{t('Sales start')}</div>
          <RadioGroupField
            value={watchSalesStart ? 'Specifies' : 'Unspecified'}
            onChange={e => {
              setValue('salesStart', e.target.value === 'Unspecified' ? undefined : { day: 0 })
            }}
            items={[
              { value: 'Unspecified', label: t('Unspecified') },
              { value: 'Specifies', label: t('Specifies') },
            ]}
            style={{ radioSize: 18, labelMargin: 12 }}
            itemCss={{ fontSize: 12 }}
            groupCss={css({ '> div:first-of-type': { marginRight: 28 } })}
          />
        </div>
        {watchSalesStart && (
          <div className="day-input-block">
            <Controller
              name="salesStart.day"
              control={control}
              render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                <InputField
                  marginBottom={24}
                  placeholder="0"
                  type="number"
                  value={value}
                  name={name}
                  handleChangeData={onChange}
                  error={error?.message}
                  width="60px"
                  fieldWidth="60px"
                />
              )}
            />
            <div className="label">{t('days before')}</div>
          </div>
        )}
      </div>
      <div>
        <div className="radio-block">
          <div className="label">{t('Sales end')}</div>
          <RadioGroupField
            value={watchSalesEnd ? 'Specifies' : 'Unspecified'}
            onChange={e => {
              setValue('salesEnd', e.target.value === 'Unspecified' ? undefined : { day: 0, hour: 1 })
            }}
            items={[
              { value: 'Unspecified', label: t('Unspecified') },
              { value: 'Specifies', label: t('Specifies') },
            ]}
            style={{ radioSize: 18, labelMargin: 12 }}
            itemCss={{ fontSize: 12 }}
            groupCss={css({ '> div:first-of-type': { marginRight: 28 } })}
          />
        </div>
        {watchSalesEnd && (
          <div className="day-input-block">
            <Trans
              i18nKey="upToTime"
              components={{
                l: <div className="label" />,
                days: (
                  <Controller
                    name="salesEnd.day"
                    control={control}
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <InputField
                        marginBottom={24}
                        placeholder="0"
                        type="number"
                        value={value}
                        name={name}
                        handleChangeData={onChange}
                        error={error?.message}
                        width="60px"
                        fieldWidth="60px"
                      />
                    )}
                  />
                ),
                hours: (
                  <Controller
                    name="salesEnd.hour"
                    control={control}
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <InputField
                        marginBottom={24}
                        placeholder="0"
                        type="number"
                        value={value}
                        name={name}
                        handleChangeData={onChange}
                        error={error?.message}
                        width="60px"
                        fieldWidth="60px"
                      />
                    )}
                  />
                ),
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const componentStyle = css({
  '.radio-block': {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    fontSize: 12,
    paddingBottom: 12,
    '.label': { fontSize: 12, color: '#676767', width: 52 },
  },
  '.day-input-block': {
    paddingLeft: 52 + 16,
    display: 'flex',
    gap: 8,
    '.label': { fontSize: 12, color: '#676767', lineHeight: '32px' },
  },
  paddingBottom: 12,
})
const inputTitleTextStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
  paddingBottom: 12,
})
